import { createApi } from '@reduxjs/toolkit/query/react';
import { 
  PushTemplate,
  PushTemplateType,
  GetPushTemplatesResponse,
  CreatePushTemplateBody,
  UpdatePushTemplateBody,
  GetPushHistoryResponse,
  GetPushHistoryParams,
  GetUserPushHistoryResponse,
  GetUserPushHistoryParams
} from '../../resources/types/pushTemplatesTypes';
import baseQuery from './baseQuery';

export const pushTemplatesApi = createApi({
  reducerPath: 'pushTemplatesApi',
  baseQuery,
  tagTypes: ['PushTemplate', 'PushHistory', 'UserPushHistory'],
  endpoints: builder => ({
    getPushTemplates: builder.query<PushTemplate[], void>({
      query: () => 'notifications/push_notifications/templates',
      transformResponse(response: GetPushTemplatesResponse) {
        console.log('=== GET /notifications/push_notifications/templates response:', response);
        const backendTemplates = response.data?.templates || [];
        return backendTemplates.map(template => {
          const lines = template.text?.split('\n') || [];
          return {
            _id: template._id,
            title: lines[0] || '',
            body: lines.slice(1).join('\n') || template.text || '',
            type: 'custom' as PushTemplateType,
            createdAt: template.createdAt,
            updatedAt: template.updatedAt
          };
        });
      },
      providesTags: ['PushTemplate']
    }),
    createPushTemplate: builder.mutation<PushTemplate, CreatePushTemplateBody>({
      query: (body) => {
        const backendBody = {
          text: `${body.title}\n${body.body}`,
          userType: null
        };
        console.log('=== POST /notifications/push_notifications/templates body:', backendBody);
        return {
          url: 'notifications/push_notifications/templates',
          method: 'POST',
          body: backendBody
        };
      },
      transformResponse(response: any) {
        console.log('=== POST /notifications/push_notifications/templates response:', response);
        const template = response?.data || response;
        const lines = template.text?.split('\n') || [];
        return {
          _id: template._id,
          title: lines[0] || '',
          body: lines.slice(1).join('\n') || template.text || '',
          type: 'custom' as PushTemplateType,
          createdAt: template.createdAt,
          updatedAt: template.updatedAt
        };
      },
      invalidatesTags: ['PushTemplate']
    }),
    updatePushTemplate: builder.mutation<PushTemplate, { id: string; body: UpdatePushTemplateBody }>({
      query: ({ id, body }) => {
        const backendBody = {
          text: `${body.title}\n${body.body}`,
          userType: null
        };
        console.log('=== PUT /notifications/push_notifications/templates/:id body:', { id, backendBody });
        return {
          url: `notifications/push_notifications/templates/${id}`,
          method: 'PUT',
          body: backendBody
        };
      },
      transformResponse(response: any) {
        console.log('=== PUT /notifications/push_notifications/templates/:id response:', response);
        const template = response?.data || response;
        const lines = template.text?.split('\n') || [];
        return {
          _id: template._id,
          title: lines[0] || '',
          body: lines.slice(1).join('\n') || template.text || '',
          type: 'custom' as PushTemplateType,
          createdAt: template.createdAt,
          updatedAt: template.updatedAt
        };
      },
      invalidatesTags: ['PushTemplate']
    }),
    deletePushTemplate: builder.mutation<void, string>({
      query: (id) => ({
        url: `notifications/push_notifications/templates/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['PushTemplate']
    }),
    getPushHistory: builder.query<GetPushHistoryResponse, GetPushHistoryParams | void>({
      query: (params) => ({
        url: 'notifications/push_notifications/admin',
        params: params || {}
      }),
      transformResponse(response: GetPushHistoryResponse) {
        console.log('=== GET /notifications/push_notifications/admin response:', response);
        return response;
      },
      providesTags: ['PushHistory']
    }),
    getUserPushHistory: builder.query<GetUserPushHistoryResponse, GetUserPushHistoryParams>({
      query: (params) => ({
        url: 'admins/push_notifications',
        params: {
          userIds: params.userIds,
          page: params.page || 1,
          limit: params.limit || 20
        }
      }),
      transformResponse(response: GetUserPushHistoryResponse) {
        console.log('=== GET /admins/push_notifications response:', response);
        return response;
      },
      providesTags: ['UserPushHistory']
    })
  })
});

export const { 
  useGetPushTemplatesQuery,
  useCreatePushTemplateMutation,
  useUpdatePushTemplateMutation,
  useDeletePushTemplateMutation,
  useGetPushHistoryQuery,
  useGetUserPushHistoryQuery
} = pushTemplatesApi;
